import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import AppContext from '../../contexts/AppContext';
import { useIssuesData } from '../../hooks/useHooks';

dayjs.locale('fr');

interface CalendarIssue {
  key: string;
  fields: {
    summary?: string;
    duedate?: string | null;
    status?: { name?: string };
    priority?: { name?: string };
  };
}

const formatGroupLabel = (dateKey: string): string => {
  const d = dayjs(dateKey);
  const today = dayjs().startOf('day');
  const diff = d.startOf('day').diff(today, 'day');

  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Demain';
  if (diff === -1) return 'Hier';
  if (diff > 1 && diff < 7) return d.format('dddd D MMMM');
  return d.format('dddd D MMMM YYYY');
};

const Calendar = () => {
  const { currentUser, serviceDesk } = useContext(AppContext) as any;
  const navigate = useNavigate();

  const { issuesDeployment, loading, error } = useIssuesData({
    userRole: currentUser?.role,
    serviceDeskConfig: serviceDesk?.config,
    maxResults: 1000,
  });

  const groups = useMemo(() => {
    const map = new Map<string, CalendarIssue[]>();
    (issuesDeployment ?? []).forEach((issue: CalendarIssue) => {
      const due = issue.fields?.duedate;
      if (!due) return;
      const key = dayjs(due).format('YYYY-MM-DD');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(issue);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [issuesDeployment]);

  const undated = useMemo(
    () => (issuesDeployment ?? []).filter((i: CalendarIssue) => !i.fields?.duedate),
    [issuesDeployment]
  );

  const todayKey = dayjs().format('YYYY-MM-DD');

  return (
    <div className="container mx-auto py-4">
      <h1 className="text-xl font-bold mb-4">Calendrier des interventions</h1>

      {loading && <p className="text-sm text-gray-500">Chargement…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && groups.length === 0 && undated.length === 0 && (
        <p className="text-sm text-gray-500">Aucun ticket avec date d'échéance.</p>
      )}

      <div className="space-y-6 pb-24">
        {groups.map(([dateKey, dayIssues]) => {
          const isToday = dateKey === todayKey;
          const isPast = dayjs(dateKey).isBefore(dayjs().startOf('day'));
          return (
            <section key={dateKey}>
              <div
                className={`flex items-baseline gap-3 border-b pb-2 mb-2 ${
                  isToday ? 'border-primary' : 'border-gray-200'
                }`}
              >
                <h2
                  className={`text-sm font-semibold capitalize ${
                    isToday ? 'text-primary' : isPast ? 'text-gray-400' : 'text-gray-800'
                  }`}
                >
                  {formatGroupLabel(dateKey)}
                </h2>
                <span className="text-xs text-gray-500">
                  {dayIssues.length} {dayIssues.length > 1 ? 'tickets' : 'ticket'}
                </span>
              </div>
              <ul className="space-y-1">
                {dayIssues.map((issue) => (
                  <li key={issue.key}>
                    <button
                      onClick={() => navigate(`/issues/${issue.key}`)}
                      className="w-full text-left flex items-center gap-3 px-3 py-2 bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-xs font-mono text-gray-500 shrink-0">
                        {issue.key}
                      </span>
                      <span className="text-sm text-gray-800 truncate flex-1">
                        {issue.fields?.summary ?? '(sans titre)'}
                      </span>
                      {issue.fields?.status?.name && (
                        <span className="text-[10px] uppercase tracking-wide text-gray-500 shrink-0">
                          {issue.fields.status.name}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        {undated.length > 0 && (
          <section>
            <div className="flex items-baseline gap-3 border-b pb-2 mb-2 border-gray-200">
              <h2 className="text-sm font-semibold text-gray-500">Sans date</h2>
              <span className="text-xs text-gray-500">
                {undated.length} {undated.length > 1 ? 'tickets' : 'ticket'}
              </span>
            </div>
            <ul className="space-y-1">
              {undated.map((issue: CalendarIssue) => (
                <li key={issue.key}>
                  <button
                    onClick={() => navigate(`/issues/${issue.key}`)}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-xs font-mono text-gray-500 shrink-0">
                      {issue.key}
                    </span>
                    <span className="text-sm text-gray-800 truncate flex-1">
                      {issue.fields?.summary ?? '(sans titre)'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

export default Calendar;
